import { parsePeriod } from '../params';
import type { Engine } from '../types';
import { buildUrl, joinQuery } from '../url';

const SITES: Record<string, string> = {
  reddit: 'site:reddit.com',
  hn: 'site:news.ycombinator.com',
  so: 'site:stackoverflow.com',
  wiki: 'site:wikipedia.org',
  edu: 'site:edu',
  gov: 'site:gov',
};

export const google: Engine = {
  id: 'google',
  name: 'Google',
  bang: 'g',
  aliases: ['google'],
  homepage: 'https://www.google.com',
  category: 'Web',
  defaultOn: true,
  description: 'Web results without the AI overview by default.',
  params: [
    {
      id: 'mode',
      type: 'choice',
      label: 'Result type',
      group: 'Type',
      help: 'Default is plain web results (no AI overview).',
      options: [
        { value: 'all', token: 'ai', label: 'All (with AI overview)' },
        { value: 'images', token: 'img', label: 'Images' },
        { value: 'videos', token: 'vid', label: 'Videos' },
        { value: 'news', token: 'news', label: 'News' },
        { value: 'shopping', token: 'shop', label: 'Shopping' },
        { value: 'books', token: 'books', label: 'Books' },
        { value: 'forums', token: 'forums', label: 'Forums' },
        { value: 'shorts', token: 'shorts', label: 'Short videos' },
        { value: 'aimode', token: 'aimode', label: 'AI Mode' },
      ],
    },
    {
      id: 'time',
      when: {
        param: 'mode',
        in: ['all', 'images', 'videos', 'news', 'books', 'forums'],
        orUnset: true,
      },
      whenLabel: 'doesn’t apply to Shopping, Short videos or AI Mode',
      type: 'period',
      label: 'Time range',
      group: 'Filters',
      units: ['h', 'd', 'w', 'm', 'y'],
      counted: true,
      help: 'Past hour/day/week/month/year. Add a count: m6 = past 6 months.',
    },
    {
      id: 'duration',
      when: { param: 'mode', in: ['videos'], orUnset: true },
      whenLabel: 'only applies to video results',
      type: 'choice',
      label: 'Video length',
      group: 'Filters',
      help: 'Switches to video results.',
      options: [
        { value: 's', token: 'short', label: 'Under 4 min' },
        { value: 'm', token: 'med', label: '4–20 min' },
        { value: 'l', token: 'long', label: 'Over 20 min' },
      ],
    },
    {
      id: 'filetype',
      when: { param: 'mode', in: ['all', 'forums'], orUnset: true },
      whenLabel: 'only applies to web results',
      type: 'choice',
      label: 'File type',
      group: 'Filters',
      operator: {
        name: 'filetype',
        kind: 'filetype',
        label: 'Or any type',
        placeholder: 'dwg',
      },
      options: [
        'pdf',
        'doc',
        'docx',
        'xls',
        'xlsx',
        'ppt',
        'pptx',
        'txt',
        'csv',
        'epub',
      ].map(ext => ({ value: ext, token: ext, label: ext.toUpperCase() })),
    },
    {
      id: 'site',
      type: 'choice',
      label: 'Site',
      group: 'Filters',
      operator: {
        name: 'site',
        kind: 'site',
        label: 'Or any site',
        placeholder: 'example.com',
      },
      options: [
        { value: 'reddit', token: 'rd', label: 'Reddit' },
        { value: 'hn', token: 'hn', label: 'Hacker News' },
        { value: 'so', token: 'so', label: 'Stack Overflow' },
        { value: 'wiki', token: 'wiki', label: 'Wikipedia' },
        { value: 'edu', token: 'edu', label: '.edu sites' },
        { value: 'gov', token: 'gov', label: '.gov sites' },
      ],
    },
    {
      id: 'verbatim',
      when: {
        param: 'mode',
        in: ['all', 'news', 'books', 'forums'],
        orUnset: true,
      },
      whenLabel: 'only applies to web, news, books and forum results',
      type: 'flag',
      token: 'verb',
      label: 'Verbatim',
      group: 'Options',
      help: 'Exact words only: no synonyms or spelling corrections.',
    },
    {
      id: 'safe',
      type: 'flag',
      token: 'safe',
      label: 'SafeSearch on',
      group: 'Options',
    },
  ],
  operators: [
    {
      name: 'intitle',
      kind: 'words',
      label: 'In the title',
      placeholder: 'release notes',
    },
    { name: 'inurl', kind: 'words', label: 'In the URL', placeholder: 'docs' },
    {
      name: 'intext',
      kind: 'words',
      label: 'In the page text',
      placeholder: 'torque spec',
    },
    {
      name: '-',
      kind: 'exclude',
      label: 'Without these words',
      placeholder: 'pinterest quora',
      help: 'Separate words with spaces; quote a phrase.',
    },
    { name: 'after', kind: 'date', label: 'Published after', placeholder: '' },
    {
      name: 'before',
      kind: 'date',
      label: 'Published before',
      placeholder: '',
    },
  ],
  build({ query, values }) {
    const tbs: string[] = [];
    if (typeof values.time === 'string') {
      const [unit, count] = parsePeriod(values.time);
      tbs.push(`qdr:${unit}${count > 1 ? count : ''}`);
    }
    if (typeof values.duration === 'string') tbs.push(`dur:${values.duration}`);
    if (values.verbatim === true) tbs.push('li:1');

    const mode = values.duration ? 'videos' : (values.mode ?? 'web');
    const vertical: Record<string, Record<string, string>> = {
      web: { udm: '14' },
      all: {},
      images: { udm: '2' },
      videos: { udm: '7' },
      news: { tbm: 'nws' },
      shopping: { udm: '28' },
      books: { udm: '36' },
      forums: { udm: '18' },
      shorts: { udm: '39' },
      aimode: { udm: '50' },
    };

    return buildUrl('https://www.google.com/search', {
      q: joinQuery(
        query,
        typeof values.filetype === 'string' && `filetype:${values.filetype}`,
        typeof values.site === 'string' && SITES[values.site]
      ),
      ...vertical[String(mode)],
      tbs: tbs.join(','),
      safe: values.safe === true && 'active',
    });
  },
};
